import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Skull, Loader2 } from 'lucide-react';
import { FactList, SubHeading, Note, type Fact } from '../GuidePanel';
import { StepList } from '../StepList';
import { NpcCard } from '../NpcCard';
import { CheckBox } from '../ChecklistBar';
import { ValidationBadge } from '../ValidationBadge';
import { QuestDropPanel } from './QuestDropPanel';
import { EntityThumb } from '../../world/EntityThumb';
import { ItemThumb } from '../../world/ItemSprite';
import { npcs } from '../../../data/guide/npcs';
import { classCodeOf, formatZen, stageName } from '../../../utils/questView';
import { characterClasses } from '../../../data/guide/classes';
import { itemPath, mapPath, monsterPath } from '../../../utils/worldFormat';
import type { Checklist } from '../../../hooks/useChecklist';
import type { WorldLookup } from '../../../utils/progressionLookup';
import type { CharacterClass } from '../../../types/progression';
import type { QuestView } from '../../../utils/questView';
type Props = {
  questId: number;
  cls?: CharacterClass;
  list: Checklist;
  prefix: string;
  lookup?: WorldLookup;
  status: string;
  steps: string[];
};
const confirmed = 'documentado' as const;

/** Requisitos, pasos y objetivos de UNA misión, desde los archivos exportados y filtrados por clase. */
export function QuestBlock({
  questId,
  cls,
  list,
  prefix,
  lookup,
  status,
  steps
}: Props) {
  if (!lookup) {
    return <p className="flex items-center gap-2 text-sm text-gray-400" data-id="element-947" data-mp="3s3dwcg6s8akg">
        <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" aria-hidden="true" data-id="element-948" data-mp="yxoeln7688a" />
        {status === 'error' ? 'No se pudieron cargar los datos del servidor.' : 'Cargando requisitos de la misión…'}
      </p>;
  }
  const v = lookup.quest(questId, cls);
  if (!v) return <Note tone="warn" data-id="element-949" data-mp="1hcxl7nqqxhrb">Esta misión no figura en los archivos de misiones cargados. Requisitos pendientes de confirmar.</Note>;
  if (cls && !v.eligible) return <Note data-id="element-950" data-mp="1ld7t55y3mi8t">{cls.base} no tiene ninguna regla de inicio para “{v.title}” en la configuración.</Note>;
  const npcId = npcs.find(n => n.monsterId === v.npcMonsterId)?.id;
  // Sin clase elegida: si cada objeto es de clases distintas, se presentan como alternativas, no como lista a reunir.
  const classSets = new Set(v.items.map(it => it.classes.map(c => c.classCode).sort().join(',')));
  const alternatives = !cls && v.items.length > 1 && classSets.size === v.items.length;
  return <div className="space-y-4" data-id="element-951" data-mp="3d0w0jar6ba9e">
      <FactList facts={facts(v, cls)} data-id="element-952" data-mp="3d15uf9fca1kd" />
      {npcId ? <NpcCard npcId={npcId} lookup={lookup} data-id="element-953" data-mp="38zu1atx9ay7f" /> : v.npcName && <p className="text-sm text-gray-300" data-id="element-954" data-mp="392gepa6tw7xk">NPC: {v.npcName}</p>}

      <SubHeading data-id="element-955" data-mp="348c84s1h61x0">Pasos</SubHeading>
      <StepList steps={steps} list={list} prefix={prefix} data-id="element-956" data-mp="347j0zpyltaec" />

      {(v.items.length > 0 || v.kills.length > 0) && <SubHeading data-id="element-957" data-mp="2z8pskby6c9l1">Objetivos</SubHeading>}
      {alternatives && <Note data-id="element-958" data-mp="m3fvdixq61y">
          <strong className="text-white" data-id="element-959" data-mp="mn1p5bphel9">Necesitas solo uno de estos objetos, según tu clase.</strong> Elige tu clase arriba para ver únicamente tus requisitos y objetivos.
        </Note>}
      {v.items.length > 0 && <ul className="grid gap-2" data-id="element-960" data-mp="83ta5jhitzqt">
          {v.items.map(it => {
        const key = `${prefix}:${it.objective.id}`;
        const have = list.isChecked(key);
        const classNames = !cls ? Array.from(new Set(it.classes.map(c => classLabel(c.classCode)))).join(', ') : undefined;
        return <li key={it.objective.id} className={`rounded-2xl border p-3.5 transition-colors duration-150 ${have ? 'border-[#D4AF37]/40 bg-[#D4AF37]/[0.05]' : 'border-white/10 bg-[#0A1628]'}`} data-id="element-961" data-mp="m4m688nh12w1">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-962" data-mp="vtdvg915d939">
                  <div className="flex min-w-0 gap-3" data-id="element-963" data-mp="10t07wg95ilb0">
                  <ItemThumb id={it.variantId ?? it.itemId} name={it.name} data-id="element-964" data-mp="10ujok7ypyydb" />
                  <div className="min-w-0" data-id="element-965" data-mp="14vwsgci94rhl">
                    <p className="flex flex-wrap items-baseline gap-x-2" data-id="element-966" data-mp="19t5e4n2lzdnl">
                      <span className="font-semibold text-white" data-id="element-967" data-mp="19r2fjene4c79">{it.name}</span>
                      <span className="text-sm text-gray-300" data-id="element-968" data-mp="w4vbhggytx1t">× {it.quantity ?? '—'}</span>
                    </p>
                    {alternatives && classNames && <p className="mt-0.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-969" data-mp="15w7zty3k2533">Solo para {classNames}</p>}
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-970" data-mp="10wwkmtiu77nm">
                      <ValidationBadge status={confirmed} label="Cantidad: confirmada por configuración" data-id="element-971" data-mp="10zr52l1uosh1" />
                      {!it.nameFromVariant && it.objective.itemLevel ? <ValidationBadge status="pendiente" label={`Variante nivel ${it.objective.itemLevel} sin nombre en el catálogo`} data-id="element-972" data-mp="97jxea4boqes" /> : null}
                    </div>
                    {it.nameSource && <p className="mt-1 text-[11px] text-gray-500" data-id="element-973" data-mp="i5vkw39w7830">Nombre: {it.nameSource}</p>}
                    {!alternatives && classNames && <p className="mt-1 text-xs text-gray-500" data-id="element-974" data-mp="1yjlu1ofvcmji">Clases: {classNames}</p>}
                    {it.drop && <QuestDropPanel drop={it.drop} data-id="element-975" data-mp="1tlkcj7y3cva5" />}
                  </div>
                  </div>
                  <div className="shrink-0" data-id="element-976" data-mp="3bidu5yepo5kj">
                    <CheckBox checked={have} onChange={() => list.toggle(key)} label="Ya lo tengo" data-id="element-977" data-mp="3gijojmcd866b" />
                  </div>
                </div>
                <Link to={`${itemPath(it.variantId ?? it.itemId)}#donde-conseguirlo`} className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-3.5 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-978" data-mp="32klgeyon15z9">
                  <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-979" data-mp="3li4p2176oml5" />
                  Dónde conseguirlo
                </Link>
              </li>;
      })}
        </ul>}
      {v.kills.length > 0 && <ul className="grid gap-2 sm:grid-cols-3" data-id="element-980" data-mp="i0e6a7eumkc6">
          {v.kills.map(k => {
        const key = `${prefix}:${k.objective.id}`;
        return <li key={k.objective.id} className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-981" data-mp="wnl89nmcw7yx">
                <p className="flex items-center gap-2" data-id="element-982" data-mp="ro8oxcup1j13">
                  <EntityThumb entity={k.monsterId !== undefined ? {
              type: 'monster',
              id: k.monsterId
            } : undefined} name={k.name} fallback={<Skull className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-984" data-mp="hq2jwmdlbws0" />} data-id="element-983" data-mp="rmp5jddqqrv3" />
                  {k.monsterId !== undefined ? <Link to={monsterPath(k.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#D4AF37]" data-id="element-985" data-mp="8xqxdbllkthb">{k.name}</Link> : <span className="font-semibold text-white" data-id="element-986" data-mp="3v60igvohab1e">{k.name}</span>}
                </p>
                <p className="mt-1 font-cinzel text-2xl font-black text-white" data-id="element-987" data-mp="3q5cl7s9noxyq">× {k.quantity ?? '—'}</p>
                <p className="text-xs text-gray-400" data-id="element-988" data-mp="3m63r2mapey2n">bajas requeridas</p>
                {k.maps.length > 0 ? <p className="mt-1.5 flex flex-wrap gap-x-2 text-xs text-gray-300" data-id="element-989" data-mp="21m8cl1v2ll21">
                    {k.maps.map(m => <Link key={m.id} to={mapPath(m.id)} className="underline decoration-white/20 underline-offset-4 hover:text-white" data-id="element-990" data-mp="25mjx8enfnldi">{m.name}</Link>)}
                  </p> : <p className="mt-1.5 text-xs text-gray-500" data-id="element-991" data-mp="2fm12iuyrvx8y">Apariciones no documentadas</p>}
                <div className="mt-2" data-id="element-992" data-mp="1i0lqen0rrmin">
                  <CheckBox checked={list.isChecked(key)} onChange={() => list.toggle(key)} label="Hecho" data-id="element-993" data-mp="1mxlx905g0tat" />
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}

/** Código de clase de los archivos → nombre de la clase inicial ("DK" → "Dark Knight"). */
const classLabel = (code: string) => characterClasses.find(c => classCodeOf(c.id) === code)?.base ?? code;
function facts(v: QuestView, cls?: CharacterClass): Fact[] {
  const out: Fact[] = [];
  if (v.levelMin !== undefined) out.push({
    label: 'Nivel mínimo',
    value: `${v.levelMin}${v.levelMax !== undefined ? ` – ${v.levelMax}` : ''}`,
    status: confirmed
  });
  if (v.zen !== undefined) out.push({
    label: 'Zen al aceptar',
    value: `${formatZen(v.zen)}${v.zenStates.length > 1 ? ' · mismo importe si se retoma tras cancelarla' : ''}`,
    status: confirmed
  });else if (!v.zenConsistent) out.push({
    label: 'Zen al aceptar',
    value: 'Importes distintos según el estado: ver panel de verificación',
    status: 'pendiente'
  });
  if (cls) {
    const s = v.starts[0];
    const stage = stageName(cls, s?.classes[0]?.changeUpPlusOne);
    if (stage) out.push({
      label: 'Clase requerida',
      value: stage,
      status: confirmed
    });
    out.push({
      label: 'Misión previa',
      value: s?.prereqTitle ? `${s.prereqTitle} (terminada)` : 'Ninguna',
      status: confirmed
    });
  } else {
    const groups = new Map<string, string[]>();
    v.starts.forEach(s => {
      const k = s.prereqTitle ? `${s.prereqTitle} (terminada)` : 'Ninguna';
      groups.set(k, [...(groups.get(k) ?? []), ...s.classes.map(c => c.classCode)]);
    });
    out.push({
      label: 'Misión previa',
      value: Array.from(groups.entries()).map(([k, codes]) => `${Array.from(new Set(codes)).join('/')}: ${k}`).join(' · ') || '—',
      status: confirmed
    });
  }
  return out;
}